import React from 'react'
import type { IHeaderParams, ICellEditorParams } from 'ag-grid-community'

export const AllocationHeaderRenderer: React.FC<IHeaderParams> = (params) => {
  return (
    <div className="flex items-center gap-1">
      <span className="text-gray-600">✎</span>
      <span>{params.displayName}</span>
    </div>
  )
}

export class AllocationCellEditor {
  private value: number = 0
  private readonly inputRef: React.RefObject<HTMLInputElement>
  private inputValue: string
  private params!: ICellEditorParams
  private issuanceIdMap: Record<string, number> = {}
  private setAllocations: ((prev: Record<number, number>) => Record<number, number>) | undefined
  private setHasOverAllocation: ((value: boolean) => void) | undefined
  private notionalQtyReminders: Record<number, number> = {}
  static displayName: string

  constructor () {
    this.inputRef = React.createRef()
    this.inputValue = ''
  }

  init (params: ICellEditorParams) {
    this.params = params
    this.value = params.value
    this.inputValue = params.value.toString()
    this.issuanceIdMap = params.context?.issuanceIdMap || {}
    this.setAllocations = params.context?.setAllocations
    this.setHasOverAllocation = params.context?.setHasOverAllocation
    this.notionalQtyReminders = params.context?.notionalQtyReminders || {}
  }

  getGui () {
    const input = document.createElement('input')
    input.type = 'text'
    input.className = 'w-full h-full px-2 border-none outline-none'
    input.value = this.inputValue

    input.addEventListener('input', (e) => {
      this.inputValue = (e.target as HTMLInputElement).value
    })

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        this.commitValue()
        this.params.stopEditing()
      } else if (e.key === 'Escape') {
        this.params.stopEditing()
      }
    })

    input.addEventListener('blur', () => {
      this.commitValue()
      this.params.stopEditing()
    })

    // Focus and select on mount
    setTimeout(() => {
      input.focus()
      input.select()
    }, 0)

    return input
  }

  private getCpIssuanceId (): number | null {
    const colId = this.params.column.getColId()
    const rowData = this.params.node.data

    // For vertical view (column-based)
    if (colId.startsWith('cp')) {
      return this.issuanceIdMap[colId.replace('cp', '')]
    }

    // For horizontal view (row-based)
    if (rowData?.cpId != null) {
      return rowData.cpId
    }

    return null
  }

  private validateAllocation (parsed: number) {
    const cpIssuanceId = this.getCpIssuanceId()
    if (cpIssuanceId == null) return

    // Get current allocations from context
    const currentAllocations = this.params.context?.currentAllocations || {}
    const updatedAllocations = {
      ...currentAllocations,
      [cpIssuanceId]: parsed
    }

    // Check if any allocation exceeds its limit
    const hasAnyOverAllocation = Object.entries(updatedAllocations).some(([id, allocation]) => {
      const notionalQtyReminder = this.notionalQtyReminders[Number(id)] || 0
      return Number(allocation) > notionalQtyReminder
    })

    if (this.setHasOverAllocation) {
      this.setHasOverAllocation(hasAnyOverAllocation)
    }
  }

  private commitValue () {
    const parsed = parseFloat(this.inputValue)
    const cpIssuanceId = this.getCpIssuanceId()

    if (!isNaN(parsed) && this.setAllocations && cpIssuanceId != null) {
      // Get current allocations from context
      const currentAllocations = this.params.context?.currentAllocations || {}
      this.setAllocations({
        ...currentAllocations,
        [cpIssuanceId]: parsed
      })
      // Validate only after committing the value
      this.validateAllocation(parsed)
    }
  }

  getValue () {
    return parseFloat(this.inputValue) || this.value
  }

  isPopup () {
    return false
  }
}

AllocationCellEditor.displayName = 'AllocationCellEditor'
